/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card } from "@material-ui/core";
import { DispositionList } from "../components/DispositionList";
import { DispositionDetails } from "../components/DispositionDetails";
import { getAllDispositions, deleteDisposition, updateDisposition } from "../services/DispositionService";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../utility/errorCodes";
import { v4 as uuid } from "uuid";
import ProgressOverlay from "../../../components/Common/ProgressOverlay";
import {
	ContentLayout,
	LeftPanelLayout,
	PanelContainer,
	RightPanelLayout,
} from "../../../components/Common/PageLayouts";
import { ShowSnackbar } from "../../../components/Common";

const COMPONENT_NAME = "Admin dispositionManager/pages/DispositionManagementPage";

const newDispositionObject = () => ({
	dispositionId: uuid(),
	dispositionName: "",
	isEnabled: true,
	subDispositions: [newSubDispositionObject()],
	new: true,
});

const newSubDispositionObject = () => ({
	subDispositionId: uuid(),
	subDispositionName: "",
	isEnabled: true,
	isDefault: true,
});

const DispositionManagementPage = () => {
	const [notification, setNotification] = React.useState({ message: "", severity: "success" });
	const [isLoading, setIsLoading] = useState(false);
	const [dispositions, setDispositions] = useState(null);
	const [selectedDisposition, setSelectedDisposition] = useState(null);
	const [formEdited, setFormEdited] = useState(false);

	useEffect(() => {
		if (selectedDisposition) {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: "smooth",
			});
		}
	}, [selectedDisposition]);

	const handleSnackBarClose = () => {
		setNotification((cur) => ({ ...cur, message: "" }));
	};

	const setSnackbar = (message, severity) => {
		setNotification({ message, severity });
	};

	const fetchDispositions = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await getAllDispositions();
			setDispositions(response || []);
		} catch (error) {
			v2.error(`Error while fetching dispositions`, {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.GET_DISPOSITION_LIST_ERROR,
			});
			setDispositions(null);
			setSnackbar("Failed to load dispositions.", "error");
		} finally {
			setIsLoading(false);
		}
	}, []);

	const createNewDisposition = () => {
		const newDisposition = newDispositionObject();
		setSelectedDisposition(newDisposition);
	};

	const removeDisposition = useCallback(
		async (dispositionId) => {
			try {
				setIsLoading(true);
				await deleteDisposition(dispositionId);
				setSelectedDisposition(null);
				setSnackbar("Successfully removed disposition.", "success");
				await fetchDispositions();
			} catch (error) {
				v2.error(`Error while removing disposition`, {
					additionalData: { error: errorToObject(error), dispositionId },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.DELETE_DISPOSITION_ERROR,
				});
				setDispositions(null);
				setSnackbar("Failed to remove disposition.", "error");
			} finally {
				setIsLoading(false);
			}
		},
		[fetchDispositions],
	);

	const saveDisposition = useCallback(
		async (updatedDisposition) => {
			try {
				setIsLoading(true);
				const isNewDisposition = updatedDisposition.new;
				delete updatedDisposition.new;
				await updateDisposition(updatedDisposition);
				setSelectedDisposition(null);
				setSnackbar(`Successfully ${isNewDisposition ? "created" : "saved"} disposition.`, "success");
				await fetchDispositions();
			} catch (error) {
				v2.error(`Error while saving disposition`, {
					additionalData: { error: errorToObject(error), disposition: updatedDisposition },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.UPDATE_DISPOSITION_ERROR,
				});
				setDispositions(null);
				setSnackbar("Failed to save disposition.", "error");
			} finally {
				setIsLoading(false);
			}
		},
		[fetchDispositions],
	);

	useEffect(() => {
		fetchDispositions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ContentLayout>
			<ShowSnackbar severity={notification.severity} message={notification.message} onClose={handleSnackBarClose} />
			{isLoading && <ProgressOverlay />}
			{!dispositions && !isLoading && (
				<div className="mt-4">
					<span className="text-danger mr-2">Failed to load dispositions, please try again.</span>
				</div>
			)}
			<PanelContainer>
				<LeftPanelLayout>
					<Card style={{ minHeight: 600, height: "100%", padding: "10px 5px" }}>
						<DispositionList
							dispositions={dispositions}
							addDispositionHandler={createNewDisposition}
							selectDispositionHandler={setSelectedDisposition}
							selectedDispositionId={selectedDisposition?.dispositionId}
							formEdited={formEdited}
						/>
					</Card>
				</LeftPanelLayout>
				<RightPanelLayout>
					<Card>
						<DispositionDetails
							disposition={selectedDisposition}
							removeDispositionHandler={removeDisposition}
							saveDispositionHandler={saveDisposition}
							cancelHandler={() => setSelectedDisposition(null)}
							formEdited={formEdited}
							setFormEdited={setFormEdited}
						/>
					</Card>
				</RightPanelLayout>
			</PanelContainer>
		</ContentLayout>
	);
};

export default DispositionManagementPage;
